<script setup lang="ts">
import { computed, ref } from 'vue';
import { useInjectGameEngine } from '@/composables/useGameEngine';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import BaseDialog from './BaseDialog.vue';
import BaseButton from './BaseButton.vue';
import { HitStatus } from '@/types';

const emit = defineEmits<{
  /**
   * Fired when the toggle needs playback paused before showing the
   * mid-piece confirmation dialog. The parent decides how — locally with
   * `gameEngine.stopPlayback()` in single-player, or with a broadcast
   * pause in classroom contexts so participants stop in sync with the
   * host's confirmation dialog.
   */
  (e: 'pause'): void;
}>();

const gameEngine = useInjectGameEngine();
const showPreviewModeConfirm = ref(false);

// Any note already judged means there's score / combo / coloring to lose
// when switching into preview mode.
const hasJudgedNotes = computed(() =>
  gameEngine.currentGameNotes.value.some(
    (note) =>
      note.hitStatus === HitStatus.Missed ||
      note.hitStatus === HitStatus.Good ||
      note.hitStatus === HitStatus.Perfect,
  ),
);

const handlePreviewToggle = () => {
  // Turning preview OFF never needs confirmation — there's no scored progress
  // accumulated during preview that would be lost.
  if (gameEngine.previewMode.value) {
    gameEngine.previewMode.value = false;
    return;
  }

  // Turning preview ON. If the user has played through any part of the piece,
  // pause first then ask for confirmation since scores will be voided.
  if (hasJudgedNotes.value) {
    if (gameEngine.isPlaying.value) {
      emit('pause');
    }
    showPreviewModeConfirm.value = true;
    return;
  }

  gameEngine.previewMode.value = true;
};

const confirmEnablePreviewMode = () => {
  gameEngine.resetNoteHitHistory();
  gameEngine.previewMode.value = true;
  showPreviewModeConfirm.value = false;
};

const cancelEnablePreviewMode = () => {
  showPreviewModeConfirm.value = false;
};
</script>

<template>
  <div class="preview-mode-row">
    <div class="preview-mode-text">
      <span class="preview-mode-label">Preview mode</span>
      <span class="preview-mode-caption">Plays through without scoring or hit detection.</span>
    </div>
    <USwitch
      :model-value="gameEngine.previewMode.value"
      aria-label="Preview mode"
      :ui="{ base: 'data-[state=checked]:bg-text-primary' }"
      @update:model-value="handlePreviewToggle"
    />
  </div>

  <BaseDialog
    v-model:is-visible="showPreviewModeConfirm"
    @close="cancelEnablePreviewMode"
  >
    <template #title>
      <ExclamationTriangleIcon class="w-6 h-6 text-warning" />
      Switch to preview mode?
    </template>

    <p class="mb-6 text-text-secondary">
      You've already played part of this piece. Switching to preview mode will void your current
      score, combo, and note progress. The piece will continue from where it is, just without
      scoring.
    </p>

    <div class="flex justify-end gap-3">
      <BaseButton
        variant="text"
        @click="cancelEnablePreviewMode"
      >
        Cancel
      </BaseButton>
      <BaseButton
        color="primary"
        @click="confirmEnablePreviewMode"
      >
        Switch to preview
      </BaseButton>
    </div>
  </BaseDialog>
</template>

<style scoped>
.preview-mode-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 44px;
}

.preview-mode-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.preview-mode-label {
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.preview-mode-caption {
  font-size: 0.75rem;
  line-height: 1.35;
  color: var(--color-text-tertiary, var(--color-text-secondary));
}
</style>
